import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface ToolCallBadgeProps {
  toolName?: string
  content: string
  resultContent?: string
}

export function ToolCallBadge({ toolName, content, resultContent }: ToolCallBadgeProps) {
  const [expanded, setExpanded] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  let prettyContent = content
  try {
    const parsed = JSON.parse(content)
    prettyContent = JSON.stringify(parsed, null, 2)
  } catch {
    // If not valid JSON, use as-is
  }

  let prettyResultContent = resultContent
  if (resultContent) {
    try {
      const parsedResult = JSON.parse(resultContent)
      prettyResultContent = JSON.stringify(parsedResult, null, 2)
    } catch {
      // If not valid JSON, use as-is
    }
  }

  const displayName = toolName || 'tool'

  useEffect(() => {
    if (!expanded || !buttonRef.current || !detailsRef.current) return

    const updatePosition = () => {
      const buttonRect = buttonRef.current!.getBoundingClientRect()
      const detailsRect = detailsRef.current!.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - buttonRect.bottom
      const spaceAbove = buttonRect.top
      const spaceRight = viewportWidth - buttonRect.left
      const spaceLeft = buttonRect.right
      const margin = 8

      let top: number
      if (spaceBelow >= detailsRect.height + margin) {
        top = buttonRect.bottom + 4
      } else if (spaceAbove >= detailsRect.height + margin) {
        top = buttonRect.top - detailsRect.height - 4
      } else if (spaceBelow > spaceAbove) {
        top = Math.max(margin, viewportHeight - detailsRect.height - margin)
      } else {
        top = margin
      }

      let left: number
      if (spaceRight >= detailsRect.width) {
        left = buttonRect.left
      } else if (spaceLeft >= detailsRect.width) {
        left = buttonRect.right - detailsRect.width
      } else {
        left = Math.max(margin, Math.min(buttonRect.left, viewportWidth - detailsRect.width - margin))
      }

      setPosition({ top, left })
    }

    requestAnimationFrame(() => {
      updatePosition()
    })

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [expanded])

  useEffect(() => {
    if (!expanded) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [expanded])

  useEffect(() => {
    if (!expanded) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [expanded])

  const portalContent =
    expanded &&
    createPortal(
      <div
        ref={detailsRef}
        className="tool-call-badge-details"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <pre className="tool-call-code">
          <code>{prettyContent}</code>
        </pre>
        {prettyResultContent ? (
          <>
            <div className="tool-call-result-heading">Result</div>
            <pre className="tool-call-code tool-call-result">
              <code>{prettyResultContent}</code>
            </pre>
          </>
        ) : null}
      </div>,
      document.body,
    )

  return (
    <span className="tool-call-badge">
      <button
        ref={buttonRef}
        className="tool-call-badge-button"
        onClick={() => setExpanded(!expanded)}
        type="button"
        title={expanded ? 'Hide details' : 'Show details'}
      >
        {displayName}
      </button>
      {portalContent}
    </span>
  )
}
